// Service tokens page — view + copy the Shanyrak sub-tokens wired into
// evimiz-core envs. The whole point is visibility: operator sees which
// service uses which JWT, can copy it once and paste into new service
// bootstraps (face-recognition, etc.) without touching kubectl.
//
// Rotation intentionally NOT here — see docs on `adminServiceTokens`
// resolver. Tokens in this model live for years.

import { useEffect, useState } from "react";
import { gql } from "../lib/gql";

type ServiceToken = {
  name: string;
  envVar: string;
  secretKey: string;
  description: string;
  jwt: string;
  tokenUUID: string | null;
  expireAt: string | null;
};

const LIST_QUERY = `
  query AdminServiceTokens {
    adminServiceTokens {
      name
      envVar
      secretKey
      description
      jwt
      tokenUUID
      expireAt
    }
  }
`;

export function ServiceTokensPage() {
  const [tokens, setTokens] = useState<ServiceToken[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const data = await gql<{ adminServiceTokens: ServiceToken[] }>(LIST_QUERY);
        setTokens(data.adminServiceTokens);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  }, []);

  function reveal(name: string) {
    setRevealed((r) => ({ ...r, [name]: true }));
  }

  async function copy(name: string, jwt: string) {
    try {
      await navigator.clipboard.writeText(jwt);
      setCopied(name);
      setTimeout(() => setCopied(null), 2000);
    } catch {
      window.prompt("Copy вручную (Cmd+C / Ctrl+C):", jwt);
    }
  }

  function maskedJWT(jwt: string): string {
    if (jwt.length < 20) return jwt;
    return `${jwt.slice(0, 8)}…${jwt.slice(-8)}`;
  }

  return (
    <div>
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>Сервисные токены Shanyrak</h2>
      </div>

      <div className="card" style={{ marginBottom: 16, padding: 16 }}>
        <p className="muted" style={{ margin: 0, lineHeight: 1.5 }}>
          Sub-токены в мета-компании Shanyrak. Каждая запись = отдельный JWT, evimiz-core читает
          из переменных окружения, аудит Shanyrak различает сервисы по их <code>tokenUUID</code>.
          Ротация не через UI — токены живут годами (Sputnik convention). Создание новых:{" "}
          <code>tools/service_tokens/create.go</code> в Shanyrak репо.
        </p>
      </div>

      {error && <div className="error-banner">{error}</div>}

      {tokens.length === 0 && !error ? (
        <div className="card muted">Нет записанных сервисных токенов.</div>
      ) : (
        tokens.map((t) => (
          <div className="card" key={t.name} style={{ marginBottom: 16, padding: 16 }}>
            <div
              className="row"
              style={{ justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}
            >
              <div>
                <div style={{ fontWeight: 600, fontSize: 16 }}>{t.name}</div>
                <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                  {t.description}
                </div>
              </div>
              {t.jwt === "" ? (
                <span className="badge inactive">не задеплоен</span>
              ) : (
                <span className="badge engineer">активен</span>
              )}
            </div>

            <div className="row" style={{ gap: 24, fontSize: 13, marginBottom: 12 }}>
              <div>
                <div className="muted">env</div>
                <code style={{ fontSize: 12 }}>{t.envVar}</code>
              </div>
              <div>
                <div className="muted">geo-secrets key</div>
                <code style={{ fontSize: 12 }}>{t.secretKey}</code>
              </div>
              {t.tokenUUID && (
                <div>
                  <div className="muted">tokenUUID</div>
                  <code style={{ fontSize: 12 }}>{t.tokenUUID}</code>
                </div>
              )}
              {t.expireAt && (
                <div>
                  <div className="muted">expires</div>
                  <code style={{ fontSize: 12 }}>
                    {new Date(t.expireAt).toLocaleDateString("ru-RU")}
                  </code>
                </div>
              )}
            </div>

            {t.jwt !== "" && (
              <div
                className="row"
                style={{
                  gap: 8,
                  padding: "8px 12px",
                  background: "var(--bg-elevated)",
                  border: "1px solid var(--border)",
                  borderRadius: 4,
                  alignItems: "center",
                }}
              >
                <code
                  style={{
                    flex: 1,
                    fontFamily: "monospace",
                    fontSize: 12,
                    wordBreak: "break-all",
                    userSelect: revealed[t.name] ? "text" : "none",
                  }}
                >
                  {revealed[t.name] ? t.jwt : maskedJWT(t.jwt)}
                </code>
                {!revealed[t.name] && (
                  <button className="secondary" onClick={() => reveal(t.name)}>
                    Показать
                  </button>
                )}
                <button onClick={() => copy(t.name, t.jwt)}>
                  {copied === t.name ? "Скопировано" : "Copy"}
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
